/**
 * iter198v (Feb 2026) — Inline editor for the vehicle make + model
 * shown on every job-context header (Pricing & Scope Builder, FOH job
 * view, tester job card). Tapping the value reveals an input pair so
 * FOH / techs can correct DVSA's "Unknown" placeholder without
 * navigating back to intake.
 *
 * Props:
 *   jobId   — required, used as the PATCH target.
 *   make    — current make string (display + initial value).
 *   model   — current model string (display + initial value).
 *   onSaved — optional callback fired after a successful PATCH so the
 *             parent can refresh its `data.job` snapshot.
 *
 * Backend contract:
 *   PATCH /api/jobs/:id  body { make, model }  (iter198v added these
 *   to JobMileageUpdate; the endpoint also propagates the change into
 *   the inspection + mot_outcomes documents).
 */
import { useState } from "react";
import axios from "axios";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import {
  formatVehicleName,
  hasUsableVehicleModel,
} from "@/utils/vehicleName";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VehicleMakeModelEditor({ jobId, make, model, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draftMake, setDraftMake] = useState(make || "");
  const [draftModel, setDraftModel] = useState(model || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const display = formatVehicleName(make, model) || "Vehicle details to confirm";
  const modelMissing = !hasUsableVehicleModel(model);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraftMake(make || "");
          setDraftModel(hasUsableVehicleModel(model) ? model : "");
          setEditing(true);
          setError(null);
        }}
        className={`inline-flex items-center gap-1.5 hover:bg-slate-50 rounded px-1 -mx-1 transition-colors ${
          modelMissing ? "text-amber-700" : ""
        }`}
        data-testid="vehicle-makemodel-edit-trigger"
        title="Click to edit make / model"
      >
        <span>{display}</span>
        {modelMissing && (
          <span className="text-[10.5px] uppercase tracking-wider font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
            Add model
          </span>
        )}
        <Pencil className="w-3.5 h-3.5 text-slate-400" />
      </button>
    );
  }

  const handleSave = async () => {
    const m = (draftMake || "").trim();
    const md = (draftModel || "").trim();
    if (!m && !md) {
      setError("Enter make or model");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/api/jobs/${jobId}`,
        { make: m || null, model: md || null },
      );
      setEditing(false);
      if (onSaved) onSaved({ make: m, model: md });
    } catch (e) {
      setError("Could not save — please retry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        type="text"
        value={draftMake}
        onChange={(e) => setDraftMake(e.target.value)}
        placeholder="Make"
        className="text-slate-900 font-semibold bg-white border border-slate-300 rounded px-2 py-0.5 w-24 text-[14px] focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
        data-testid="vehicle-makemodel-make-input"
      />
      <input
        type="text"
        value={draftModel}
        onChange={(e) => setDraftModel(e.target.value)}
        placeholder="Model"
        className="text-slate-900 font-semibold bg-white border border-slate-300 rounded px-2 py-0.5 w-36 text-[14px] focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
        data-testid="vehicle-makemodel-model-input"
        autoFocus
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-50"
        data-testid="vehicle-makemodel-save"
      >
        {saving ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Check className="w-3.5 h-3.5" />
        )}
        Save
      </button>
      <button
        type="button"
        onClick={() => {
          setEditing(false);
          setError(null);
        }}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50"
        data-testid="vehicle-makemodel-cancel"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {error && <span className="text-xs text-red-600 ml-1">{error}</span>}
    </span>
  );
}
